// Crate types are not an enum: they are strings embedded in the `type__STATUS` keys
// stored in prod_mvt_crates and prod_inv_crates. The list of them was copy-pasted
// into every declaration form, which is why adding 7.1/7.2/LIDS had to touch five
// separate <option> blocks. New code reads it from here.

// The types every plant can hold. '1R'/'1RB' and '5.X' are configuration-only:
// see DECLARABLE_EXCLUSIONS below.
export const UNIVERSAL_CRATE_TYPES = [
  { name: '1', value: '1' },
  { name: '1R', value: '1R' },
  { name: '1RB', value: '1RB' },
  { name: '5.1', value: '5.1' },
  { name: '5.2', value: '5.2' },
  { name: '5.5', value: '5.5' },
  { name: '5.X', value: '5.X' },
  { name: '7.1', value: '7.1' },
  { name: '7.2', value: '7.2' },
  { name: 'LIDS', value: 'LIDS' }
]

// Older client types that each got their own SAP material, so SAP identifies them
// directly and they need none of the partner inference the registry does. They stay
// as they are; the registry is only for types SAP cannot tell apart.
export const LEGACY_CLIENT_TYPES = [
  { name: 'GPS Food', value: 'GPSFood' },
  { name: 'ZEON', value: 'ZEON' },
  { name: 'RHODIA', value: 'RHODIA' }
]

// '5.X' is a reporting pool: a site configured with it holds 5.2 and 5.5 as one
// interchangeable stock, and the forecast expands it into those two before doing any
// stock math. A movement or inventory written against '5.X__DIRTY' would be stored
// and then ignored for ever, so it must never appear in a declaration form.
export const VIRTUAL_TYPE_MEMBERS = { '5.X': ['5.2', '5.5'] }

// Every type a plant may configure, the pools included.
export function configurableTypes (clientTypes = []) {
  return [
    ...UNIVERSAL_CRATE_TYPES,
    ...LEGACY_CLIENT_TYPES,
    ...clientTypes.map(t => ({ name: t.name, value: t.name }))
  ]
}

// Root types a client-specific type may be based on: real types only, so no pool.
export function rootTypeOptions () {
  return UNIVERSAL_CRATE_TYPES.filter(t => !VIRTUAL_TYPE_MEMBERS[t.value])
}

// The types a plant can actually declare against: its configured list with the pools
// expanded into their members, plus the client types its registry rows grant it.
//
// `siteTypes` is prod_target_stock.types as the API returns it (already merged with
// the plant's client types), either as [{name}] or as plain strings.
export function declarableTypes ({ siteTypes = [], clientTypes = [] } = {}) {
  const names = siteTypes.map(t => (typeof t === 'string' ? t : t.name)).filter(Boolean)
  const expanded = []
  for (const name of names) {
    const members = VIRTUAL_TYPE_MEMBERS[name]
    if (members) {
      for (const member of members) if (!expanded.includes(member)) expanded.push(member)
    } else if (!expanded.includes(name)) {
      expanded.push(name)
    }
  }
  // A plant with nothing configured still has to be able to declare something.
  if (expanded.length === 0) {
    for (const t of UNIVERSAL_CRATE_TYPES) {
      if (!VIRTUAL_TYPE_MEMBERS[t.value]) expanded.push(t.value)
    }
    for (const t of LEGACY_CLIENT_TYPES) expanded.push(t.value)
  }
  for (const clientType of clientTypes) {
    if (!expanded.includes(clientType.name)) expanded.push(clientType.name)
  }
  const labels = new Map([...UNIVERSAL_CRATE_TYPES, ...LEGACY_CLIENT_TYPES].map(t => [t.value, t.name]))
  return expanded.map(value => ({ name: labels.get(value) || value, value }))
}

// What a type is, for the forms and the forecast page. Mirrors buildDescriptors in
// the API's helpers/crateTypes.js.
export function describeType (name, clientTypes = []) {
  const clientType = clientTypes.find(t => t.name === name)
  if (clientType) return { name, kind: 'client', root: clientType.rootType, meanEnabled: false }
  return { name, kind: 'universal', root: null, meanEnabled: true }
}

export function isClientType (name, clientTypes = []) {
  return clientTypes.some(t => t.name === name)
}
